import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";

// Import all components directly (avoid React.lazy to prevent duplicate React contexts)
import Header from "./components/Header";
import Footer from "./components/Footer";
import Index from "./pages/Index";
import HomeV2 from "./pages/HomeV2";
import HomeOriginal from "./pages/archive/HomeOriginal";
import HomeLegacy from "./pages/archive/HomeLegacy";
import DemandGeneration from "./pages/DemandGeneration";
import AccountBasedMarketing from "./pages/AccountBasedMarketing";
import PipelineAcceleration from "./pages/PipelineAcceleration";
import About from "./pages/About";
import Culture from "./pages/Culture";
import OurCommitments from "./pages/OurCommitments";
import Pricing from "./pages/Pricing";
import WebsiteDesign from "./pages/WebsiteDesign";
import Resources from "./pages/Resources";
import Blog from "./pages/Blog";
import WhitePapers from "./pages/WhitePapers";
import ContentPolicy from "./pages/ContentPolicy";
import FAQ from "./pages/FAQ";
import SEOLocalSearch from "./pages/SEOLocalSearch";
import SEOCaseStudy from "./pages/SEOCaseStudy";
import BookCall from "./pages/BookCall";
import PaidAds from "./pages/PaidAds";
import GrowthStart from "./pages/GrowthStart";
import BusinessAccelerator from "./pages/BusinessAccelerator";
import EnterpriseDomination from "./pages/EnterpriseDomination";
import Enterprise from "./pages/Enterprise";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import PersonalizedGrowthPlan from "./pages/PersonalizedGrowthPlan";
import LeadsAnalytics from "./pages/LeadsAnalytics";
import AdminDashboard from "./pages/AdminDashboard";
import LinkedInSDR from "./pages/LinkedInSDR";
import ThankYouBooking from "./pages/ThankYouBooking";
import ProtectedRoute from "./components/ProtectedRoute";
import ExitIntentPopup from "./components/ExitIntentPopup";
import StickyBookingButton from "./components/StickyBookingButton";
import FloatingTrustpilot from "./components/FloatingTrustpilot";

const queryClient = new QueryClient();

// Global handler to redirect recovery links to /auth
const RecoveryRedirectHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if URL hash contains recovery token and we're not already on /auth
    if (window.location.hash.includes("type=recovery") && location.pathname !== "/auth") {
      // Preserve the hash when redirecting to /auth
      navigate(`/auth${window.location.hash}`, { replace: true });
    }
  }, [navigate, location.pathname]);

  return null;
};

// Layout wrapper for main site pages
const MainLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen flex flex-col">
    <Header />
    <main id="main-content" className="flex-1 pt-16" tabIndex={-1}>
      {children}
    </main>
    <Footer />
    <ExitIntentPopup />
    <StickyBookingButton />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Toaster />
    <Sonner />
    <BrowserRouter>
      <RecoveryRedirectHandler />
      <Routes>
        {/* Standalone funnel pages - no header/footer */}
        {/* Main qualifier funnel */}
        <Route path="/do-you-qualify" element={<PersonalizedGrowthPlan source="organic" />} />
        
        {/* Source-tracked funnel pages (both /source and /do-you-qualify/source formats) */}
        <Route path="/fb" element={<PersonalizedGrowthPlan source="facebook" />} />
        <Route path="/do-you-qualify/fb" element={<PersonalizedGrowthPlan source="facebook" />} />
        <Route path="/ig" element={<PersonalizedGrowthPlan source="instagram" />} />
        <Route path="/do-you-qualify/ig" element={<PersonalizedGrowthPlan source="instagram" />} />
        <Route path="/li" element={<PersonalizedGrowthPlan source="linkedin" />} />
        <Route path="/do-you-qualify/li" element={<PersonalizedGrowthPlan source="linkedin" />} />
        <Route path="/tw" element={<PersonalizedGrowthPlan source="twitter" />} />
        <Route path="/do-you-qualify/tw" element={<PersonalizedGrowthPlan source="twitter" />} />
        <Route path="/yt" element={<PersonalizedGrowthPlan source="youtube" />} />
        <Route path="/do-you-qualify/yt" element={<PersonalizedGrowthPlan source="youtube" />} />
        <Route path="/gads" element={<PersonalizedGrowthPlan source="google-ads" />} />
        <Route path="/do-you-qualify/gads" element={<PersonalizedGrowthPlan source="google-ads" />} />
        <Route path="/email" element={<PersonalizedGrowthPlan source="email" />} />
        <Route path="/do-you-qualify/email" element={<PersonalizedGrowthPlan source="email" />} />
        <Route path="/ref" element={<PersonalizedGrowthPlan source="referral" />} />
        <Route path="/do-you-qualify/ref" element={<PersonalizedGrowthPlan source="referral" />} />
        <Route path="/tt" element={<PersonalizedGrowthPlan source="tiktok" />} />
        <Route path="/do-you-qualify/tt" element={<PersonalizedGrowthPlan source="tiktok" />} />
        <Route path="/pin" element={<PersonalizedGrowthPlan source="pinterest" />} />
        <Route path="/do-you-qualify/pin" element={<PersonalizedGrowthPlan source="pinterest" />} />
        
        {/* Thank You page after booking confirmation */}
        <Route path="/thank-you-booking" element={<ThankYouBooking />} />
        
        <Route path="/app" element={<LeadsAnalytics />} />

        {/* Main site with header/footer */}
        <Route path="/" element={<MainLayout><HomeV2 /></MainLayout>} />
        
        {/* Homepage Archives - for LinkedIn case study on website evolution */}
        <Route path="/archive/home-original" element={<MainLayout><HomeOriginal /></MainLayout>} />
        <Route path="/archive/home-legacy" element={<MainLayout><HomeLegacy /></MainLayout>} />
        <Route path="/home-legacy" element={<MainLayout><HomeLegacy /></MainLayout>} />
        <Route path="/about" element={<MainLayout><About /></MainLayout>} />
        <Route path="/culture" element={<MainLayout><Culture /></MainLayout>} />
        <Route path="/our-commitments" element={<MainLayout><OurCommitments /></MainLayout>} />
        <Route path="/pricing" element={<MainLayout><Pricing /></MainLayout>} />
        <Route path="/website-design" element={<MainLayout><WebsiteDesign /></MainLayout>} />
        <Route path="/resources" element={<MainLayout><Resources /></MainLayout>} />
        <Route path="/blog" element={<MainLayout><Blog /></MainLayout>} />
        <Route path="/white-papers" element={<MainLayout><WhitePapers /></MainLayout>} />
        <Route path="/enterprise" element={<MainLayout><Enterprise /></MainLayout>} />
        <Route path="/demand-generation" element={<MainLayout><DemandGeneration /></MainLayout>} />
        <Route path="/abm" element={<MainLayout><AccountBasedMarketing /></MainLayout>} />
        <Route path="/pipeline-acceleration" element={<MainLayout><PipelineAcceleration /></MainLayout>} />
        <Route path="/paid-ads" element={<MainLayout><PaidAds /></MainLayout>} />
        <Route path="/linkedin-sdr" element={<LinkedInSDR />} />
        <Route path="/seo-local-search" element={<MainLayout><SEOLocalSearch /></MainLayout>} />
        <Route path="/seo-case-study" element={<MainLayout><SEOCaseStudy /></MainLayout>} />
        <Route path="/book-call" element={<MainLayout><BookCall /></MainLayout>} />
        <Route path="/content-policy" element={<MainLayout><ContentPolicy /></MainLayout>} />
        <Route path="/faq" element={<MainLayout><FAQ /></MainLayout>} />
        <Route path="/growth-start" element={<MainLayout><GrowthStart /></MainLayout>} />
        <Route path="/business-accelerator" element={<MainLayout><BusinessAccelerator /></MainLayout>} />
        <Route path="/enterprise-domination" element={<MainLayout><EnterpriseDomination /></MainLayout>} />
        <Route path="/privacy-policy" element={<MainLayout><PrivacyPolicy /></MainLayout>} />
        <Route path="/terms-of-service" element={<MainLayout><TermsOfService /></MainLayout>} />
        <Route path="/auth" element={<MainLayout><Auth /></MainLayout>} />
        <Route 
          path="/admin" 
          element={
            <MainLayout>
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            </MainLayout>
          } 
        />
        <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
      </Routes>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
